import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  Package,
  Loader2,
  CheckCircle,
  Eye,
  Archive,
  Filter,
  ChevronLeft,
  ChevronRight,
  FileText,
} from 'lucide-react';
import { api } from '../../lib/api';

interface Bundle {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  status: string;
  workItemCount: number;
  approvedItemCount: number;
  createdAt: string;
}

const statusConfig: Record<string, { icon: typeof Package; label: string; color: string; bg: string }> = {
  in_progress: { icon: Loader2, label: 'In Progress', color: 'text-blue-500', bg: 'bg-blue-500/10' },
  ready_for_review: { icon: Eye, label: 'Ready for Review', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  approved: { icon: CheckCircle, label: 'Approved', color: 'text-green-500', bg: 'bg-green-500/10' },
  archived: { icon: Archive, label: 'Archived', color: 'text-gray-500', bg: 'bg-gray-500/10' },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

export function BundlesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const currentStatus = searchParams.get('status') || '';
  const currentSearch = searchParams.get('search') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = 20;

  useEffect(() => {
    async function fetchBundles() {
      try {
        setIsLoading(true);
        const params = new URLSearchParams();
        if (currentStatus) params.append('status', currentStatus);
        params.append('page', currentPage.toString());
        params.append('limit', pageSize.toString());

        const response = await api.get<Bundle[]>(
          `/bundles?${params.toString()}`
        ) as any;
        setBundles(response.data || []);
        setTotalCount(response.pagination?.total || 0);
        setError(null);
      } catch (err) {
        setError('Failed to load bundles');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchBundles();
  }, [currentStatus, currentPage]);

  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const filteredBundles = bundles.filter(
    (b) =>
      b.title.toLowerCase().includes(currentSearch.toLowerCase()) ||
      b.projectName.toLowerCase().includes(currentSearch.toLowerCase())
  );

  const totalPages = Math.ceil(totalCount / pageSize);

  // Group bundles needing review
  const needsReview = filteredBundles.filter(b => b.status === 'ready_for_review');
  const otherBundles = filteredBundles.filter(b => b.status !== 'ready_for_review');

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold">Bundles</h1>
          <p className="text-text-secondary mt-1">
            Review grouped work items for complex deliverables
          </p>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-wrap items-center gap-3"
      >
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input
            type="text"
            placeholder="Search bundles..."
            value={currentSearch}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-bg-secondary border border-border rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-text-muted" />
          <select
            value={currentStatus}
            onChange={(e) => updateFilter('status', e.target.value)}
            className="px-3 py-2 bg-bg-secondary border border-border rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-accent/50"
          >
            <option value="">All Statuses</option>
            {Object.entries(statusConfig).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>
        </div>
      </motion.div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="text-center py-12">
          <p className="text-red-500">{error}</p>
        </div>
      )}

      {/* Content */}
      {!isLoading && !error && (
        <>
          {/* Needs Your Review Section */}
          {needsReview.length > 0 && !currentStatus && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Eye className="w-5 h-5 text-amber-500" />
                </div>
                <h2 className="text-lg font-semibold">Ready for Review</h2>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500">
                  {needsReview.length}
                </span>
              </div>

              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-2"
              >
                {needsReview.map((bundle) => (
                  <BundleRow key={bundle.id} bundle={bundle} highlight />
                ))}
              </motion.div>
            </motion.section>
          )}

          {/* Empty State */}
          {filteredBundles.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <Package className="w-16 h-16 mx-auto text-text-muted mb-4" />
              <h2 className="text-xl font-semibold mb-2">No bundles yet</h2>
              <p className="text-text-secondary">
                Bundles will appear here when your team creates them
              </p>
            </motion.div>
          )}

          {/* All Bundles */}
          {(currentStatus || otherBundles.length > 0) && filteredBundles.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: needsReview.length > 0 ? 0.25 : 0.15 }}
            >
              {!currentStatus && needsReview.length > 0 && (
                <h2 className="text-lg font-semibold mb-4">All Bundles</h2>
              )}

              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-2"
              >
                {(currentStatus ? filteredBundles : otherBundles).map((bundle) => (
                  <BundleRow key={bundle.id} bundle={bundle} />
                ))}
              </motion.div>
            </motion.section>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center justify-center gap-2 pt-4"
            >
              <button
                onClick={() => updateFilter('page', String(currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg bg-bg-secondary border border-border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-tertiary transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-text-secondary">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => updateFilter('page', String(currentPage + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg bg-bg-secondary border border-border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-tertiary transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}

function BundleRow({ bundle, highlight }: { bundle: Bundle; highlight?: boolean }) {
  const status = statusConfig[bundle.status];
  const StatusIcon = status?.icon || Package;
  const progress = bundle.workItemCount > 0
    ? Math.round((bundle.approvedItemCount / bundle.workItemCount) * 100)
    : 0;

  return (
    <motion.div variants={itemVariants}>
      <Link to={`/bundles/${bundle.id}`}>
        <div
          className={`p-4 bg-bg-secondary rounded-xl border transition-all hover:border-accent/30 ${
            highlight ? 'border-amber-500/30 shadow-lg shadow-amber-500/5' : 'border-border'
          }`}
        >
          <div className="flex items-center gap-4">
            {/* Icon */}
            <div className="p-2 rounded-lg bg-accent/10">
              <Package className="w-5 h-5 text-accent" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium truncate">{bundle.title}</h3>
              <div className="flex items-center gap-3 text-sm text-text-secondary">
                <span className="truncate">{bundle.projectName}</span>
                <span>-</span>
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  {bundle.workItemCount} items
                </span>
              </div>

              {/* Progress bar */}
              {bundle.workItemCount > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-bg-tertiary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="text-xs text-text-muted">
                    {bundle.approvedItemCount}/{bundle.workItemCount}
                  </span>
                </div>
              )}
            </div>

            {/* Status Badge */}
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status?.bg || 'bg-gray-500/10'} ${status?.color || 'text-gray-500'}`}>
              <StatusIcon className="w-3 h-3" />
              {status?.label || bundle.status}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
